import { useRef, useState, type ReactElement } from 'react';

import { ElevateSessionDialog } from '@/components/shared/elevate-session-dialog';
import { useTotpEnrollmentGate } from '@/hooks/use-totp-enrollment-gate';
import { isApiError } from '@/lib/api-errors';

/** Thrown when the user dismisses the step-up dialog without confirming. */
export class ElevationCancelledError extends Error {
  constructor() {
    super('ELEVATION_CANCELLED');
    this.name = 'ElevationCancelledError';
  }
}

export const isElevationCancelled = (error: unknown): boolean =>
  error instanceof ElevationCancelledError;

export interface UseElevateSessionOptions {
  /** Default explanation when opening the enrollment dialog. */
  totpEnrollmentDescription?: string;
}

/**
 * Wraps an API action so `ELEVATION_REQUIRED` opens a step-up dialog, elevates the
 * session, then retries once. When two-factor is not on, opens enrollment in place.
 */
export const useElevateSession = (
  options: UseElevateSessionOptions = {},
): {
  elevateAndRetry: <T>(
    action: () => Promise<T>,
    options?: { description?: string },
  ) => Promise<T>;
  /** Opens the step-up dialog, then runs the action when the session is elevated. */
  requireElevationThen: (action: () => void, description?: string) => void;
  requireTotpThen: (action: () => void, description?: string) => void;
  openEnrollment: (next?: { description?: string; onEnrolled?: () => void }) => void;
  isTotpEnabled: boolean;
  dialog: ReactElement;
} => {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState<string | undefined>();
  const resolveRef = useRef<((elevated: boolean) => void) | null>(null);
  const {
    requireTotpThen,
    openEnrollment,
    isTotpEnabled,
    dialog: totpEnrollmentDialog,
  } = useTotpEnrollmentGate({ description: options.totpEnrollmentDescription });

  const settle = (elevated: boolean) => {
    resolveRef.current?.(elevated);
    resolveRef.current = null;
    setOpen(false);
  };

  const waitForElevation = (nextDescription?: string) => {
    if (nextDescription !== undefined) setDescription(nextDescription);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setOpen(true);
    });
  };

  const requireElevationThen = (action: () => void, nextDescription?: string) => {
    void waitForElevation(nextDescription).then((elevated) => {
      if (elevated) action();
    });
  };

  const elevateAndRetry = async <T,>(
    action: () => Promise<T>,
    retryOptions?: { description?: string },
  ): Promise<T> => {
    try {
      return await action();
    } catch (error) {
      if (!isApiError(error) || error.code !== 'ELEVATION_REQUIRED') throw error;

      const elevated = await waitForElevation(retryOptions?.description);
      if (!elevated) throw new ElevationCancelledError();

      return await action();
    }
  };

  const elevateDialog = (
    <ElevateSessionDialog
      open={open}
      description={description}
      onOpenChange={(next) => {
        if (!next) settle(false);
        else setOpen(true);
      }}
      onElevated={() => settle(true)}
      onTotpEnrollmentRequired={() => {
        settle(false);
        openEnrollment({
          onEnrolled: () => {
            void waitForElevation(description);
          },
        });
      }}
    />
  );

  return {
    elevateAndRetry,
    requireElevationThen,
    requireTotpThen,
    openEnrollment,
    isTotpEnabled,
    dialog: (
      <>
        {elevateDialog}
        {totpEnrollmentDialog}
      </>
    ),
  };
};
